import {Component, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ChartOptions} from "@shared/chart/chart-options";
import {NgApexchartsModule} from "ng-apexcharts";
import {UserCareProgramWeek} from "@shared/api/models/query/user-care-program.model";
import {OverlaySpinnerComponent} from "@shared/components/overlay-spinner/overlay-spinner.component";
import {PatientStatisticsService} from "@shared/api/services/v2/patient-statistics.service";
import {AdherenceCharts} from "@shared/api/models/query/patient-statistics.model";
import {WeekValue} from "@shared/api/models/query/patient.model";

@Component({
    selector: 'cdss-patient-overview-v2-adherence',
    imports: [CommonModule, NgApexchartsModule, OverlaySpinnerComponent],
    templateUrl: './patient-overview-v2-adherence.component.html',
    standalone: true,
    styleUrl: './patient-overview-v2-adherence.component.scss'
})
export class PatientOverviewV2AdherenceComponent {

    private _currentWeek: UserCareProgramWeek = null
    disabled: boolean = false
    loading: boolean = false

    @Input() type: "patient" | "carer" = 'patient';
    chapterStroke: any = null
    chapterFill: any = null
    checkinStroke: any = null
    checkinFill: any = null

    @Input()
    set currentWeek(week: UserCareProgramWeek) {
        if (!week) return
        this._currentWeek = week
        this.refresh()
    }
    get currentWeek(): UserCareProgramWeek {
        return this._currentWeek
    }

    public chartOptions: Partial<ChartOptions>;
    charts: AdherenceCharts
    chapterSeries: { data: number[]; type: string, name: string }[] = [{ data: [], type: 'line', name: 'Chapters'}]
    checkinSeries: { data: number[]; type: string, name: string }[] = [{ data: [], type: 'line', name: 'Checkins'}]

    constructor(
        private patientStatisticsService: PatientStatisticsService
    ) {
        this.chartOptions = {
            series: [
                {
                    name: 'Values',
                    data: []
                }
            ],
            chart: {
                type: 'line',
                height: 300,
                toolbar: { show: false },
                zoom: {
                    enabled: false
                },
            },
            tooltip: {
                custom: function({ series, seriesIndex, dataPointIndex, w }) {
                    const value = series[0][dataPointIndex];
                    const label = w.globals.labels[dataPointIndex];

                    return `
                      <div style="padding:10px">
                        <strong>Week ${label}</strong><br/>
                        Value: ${value}%
                      </div>
                    `;
                }
            },
            xaxis: {
                categories: ['W1', 'W2', 'W3', 'W4', 'W5', 'W6', 'W7', 'W8', 'W9', 'W10', 'W11', 'W12', 'W13', 'W14', 'W15', 'W16', 'W17', 'W18', 'W19', 'W20'],
                labels: {
                    style: { fontSize: '12px' }
                }
            },
            yaxis: {
                min: 0,
                max: 100,
                tickAmount: 4,
                labels: {
                    formatter: val => `${val}%`
                }
            },
            legend: {
                show: false
            }
        };

        // UNIVAPLAT-2750: was a generic teal/magenta pair from the legacy
        // theme — swapped to the univa-2026 brand teal (chapters) + navy
        // (checkins, distinct from teal without touching gold, which stays
        // identity-only per the design system rule).
        this.chapterStroke = {
            width: [4, 0],
            colors: ['#17627A']
        }
        this.chapterFill = {
            opacity: [1, 0.3],
            colors: ['#E9F1E1']
        }

        this.checkinStroke = {
            width: [4, 0],
            colors: ['#1F5C8B']
        }
        this.checkinFill = {
            opacity: [1, 0.3],
            colors: ['#E6F0F9']
        }

    }

    async refresh() {
        this.loading = true

        this.charts = await this.patientStatisticsService.getCharts(
            this._currentWeek.patientId,
            this.type
        )
        this.loading = false

        // Chapters and checkins both arrive from the backend already scaled to
        // 0-100 (PatientStatisticsService::getModulesChart averages the raw
        // 0-100 UserCareProgramModule::progress_value directly — see the
        // migration history, that column has always been a percentage, not a
        // 0-1 fraction — and getCheckinsChart multiplies by 100), so both only
        // need rounding for whole-number display — no re-scaling.
        const chapterValues = this.roundValues(this.charts?.chapters)
        const checkinValues = this.roundValues(this.charts?.checkins)

        this.chapterSeries = [
            {
                name: 'Chapters',
                type: 'line',
                data: chapterValues
            },
            {
                name: 'Chapters',
                type: 'column',
                data: chapterValues
            },
        ]
        this.checkinSeries = [
            {
                name: 'Checkins',
                type: 'line',
                data: checkinValues
            },
            {
                name: 'Checkins',
                type: 'column',
                data: checkinValues
            },
        ]

    }

    /**
     * Both chapters and checkins arrive from the backend already scaled to
     * 0-100 — only round to a whole number for display.
     */
    private roundValues(weekValues: WeekValue[] | undefined): number[] {
        if (!weekValues) return []
        return weekValues.map(weekValue => Math.round(weekValue.value ?? 0))
    }
}
